import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  Box,
  Tooltip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  RestartAlt as ResetIcon
} from '@mui/icons-material';
import type { ClimbDetectionConfig } from '../../utils/climbDetection';
import { DEFAULT_CLIMB_CONFIG } from '../../utils/climbDetection';

interface ClimbConfigModalProps {
  open: boolean;
  onClose: () => void;
  config: ClimbDetectionConfig;
  onConfigChange: (config: ClimbDetectionConfig) => void;
}

// Predefined configurations for different cycling types
const PRESET_CONFIGS = {
  road: {
    name: 'Road Cycling',
    description: 'Standard settings for road cycling',
    config: {
      ...DEFAULT_CLIMB_CONFIG,
      minElevationGain: 30,
      minDistance: 300,
      minAverageGradient: 3,
      maxDescentInClimb: 10
    }
  },
  mountain: {
    name: 'Mountain Biking',
    description: 'More sensitive settings for mountain biking',
    config: {
      ...DEFAULT_CLIMB_CONFIG,
      minElevationGain: 20,
      minDistance: 200,
      minAverageGradient: 2,
      maxDescentInClimb: 15
    }
  },
  gravel: {
    name: 'Gravel/Mixed',
    description: 'Balanced settings for mixed terrain',
    config: {
      ...DEFAULT_CLIMB_CONFIG,
      minElevationGain: 25,
      minDistance: 250,
      minAverageGradient: 2.5,
      maxDescentInClimb: 12
    }
  },
  strict: {
    name: 'Strict Detection',
    description: 'Only significant climbs',
    config: {
      ...DEFAULT_CLIMB_CONFIG,
      minElevationGain: 50,
      minDistance: 500,
      minAverageGradient: 4,
      maxDescentInClimb: 8
    }
  }
};

export const ClimbConfigModal: React.FC<ClimbConfigModalProps> = ({
  open,
  onClose,
  config,
  onConfigChange
}) => {
  const [tempConfig, setTempConfig] = useState<ClimbDetectionConfig>(config);
  const [selectedPreset, setSelectedPreset] = useState<string>('');

  const handlePresetChange = (preset: string) => {
    if (preset && PRESET_CONFIGS[preset as keyof typeof PRESET_CONFIGS]) {
      setTempConfig(PRESET_CONFIGS[preset as keyof typeof PRESET_CONFIGS].config);
      setSelectedPreset(preset);
    }
  };

  const handleReset = () => {
    setTempConfig(DEFAULT_CLIMB_CONFIG);
    setSelectedPreset('');
  };

  const handleSave = () => {
    onConfigChange(tempConfig);
    onClose();
  };

  const handleCancel = () => {
    setTempConfig(config);
    onClose();
  };

  const updateConfigValue = (key: keyof ClimbDetectionConfig, value: number) => {
    setTempConfig(prev => ({ ...prev, [key]: value }));
    setSelectedPreset(''); // Clear preset when manually editing
  };

  const InfoTooltip: React.FC<{ text: string }> = ({ text }) => (
    <Tooltip title={text} placement="top">
      <IconButton size="small" sx={{ ml: 0.5 }}>
        <InfoIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Climb Detection Settings
        <IconButton onClick={handleReset} size="small" title="Reset to defaults">
          <ResetIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Preset Configuration</InputLabel>
            <Select
              value={selectedPreset}
              onChange={(e) => handlePresetChange(e.target.value)}
              label="Preset Configuration"
            >
              <MenuItem value="">
                <em>Custom Configuration</em>
              </MenuItem>
              {Object.entries(PRESET_CONFIGS).map(([key, preset]) => (
                <MenuItem key={key} value={key}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {preset.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {preset.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Basic Thresholds</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <TextField
                    fullWidth
                    label="Min Elevation Gain (m)"
                    type="number"
                    value={tempConfig.minElevationGain}
                    onChange={(e) => updateConfigValue('minElevationGain', Number(e.target.value))}
                    inputProps={{ min: 5, max: 200, step: 5 }}
                  />
                  <InfoTooltip text="Minimum elevation gain required for a climb to be detected" />
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <TextField
                    fullWidth
                    label="Min Distance (m)"
                    type="number"
                    value={tempConfig.minDistance}
                    onChange={(e) => updateConfigValue('minDistance', Number(e.target.value))}
                    inputProps={{ min: 50, max: 2000, step: 50 }}
                  />
                  <InfoTooltip text="Minimum horizontal distance required for a climb" />
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <TextField
                    fullWidth
                    label="Min Average Gradient (%)"
                    type="number"
                    value={tempConfig.minAverageGradient}
                    onChange={(e) => updateConfigValue('minAverageGradient', Number(e.target.value))}
                    inputProps={{ min: 0.5, max: 15, step: 0.5 }}
                  />
                  <InfoTooltip text="Minimum average gradient percentage for climb detection" />
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <TextField
                    fullWidth
                    label="Max Descent in Climb (m)"
                    type="number"
                    value={tempConfig.maxDescentInClimb}
                    onChange={(e) => updateConfigValue('maxDescentInClimb', Number(e.target.value))}
                    inputProps={{ min: 2, max: 50, step: 2 }}
                  />
                  <InfoTooltip text="Maximum allowed descent within a climb before it's considered finished" />
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Advanced Settings</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <TextField
                    fullWidth
                    label="Smoothing Window"
                    type="number"
                    value={tempConfig.smoothingWindow}
                    onChange={(e) => updateConfigValue('smoothingWindow', Number(e.target.value))}
                    inputProps={{ min: 1, max: 20, step: 1 }}
                  />
                  <InfoTooltip text="Number of elevation points used for smoothing (reduces noise)" />
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <TextField
                    fullWidth
                    label="Min Length for Categorization (m)"
                    type="number"
                    value={tempConfig.categorizationMinLength}
                    onChange={(e) => updateConfigValue('categorizationMinLength', Number(e.target.value))}
                    inputProps={{ min: 100, max: 2000, step: 100 }}
                  />
                  <InfoTooltip text="Minimum climb length required for category classification (Cat 4, Cat 3, etc.)" />
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Note:</strong> Changes will only affect new route calculations. 
            Existing routes will need to be recalculated to apply new settings.
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleCancel} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Apply Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClimbConfigModal;
